/**
 * Clinician-placed foot contacts. They use the same column names the pipeline
 * writes into the contact csv (heel_left_contact, toe_right_off, …).
 */

const TYPE_PARTS = {
  heel_contact: { landmark: 'heel', event: 'contact' },
  heel_off: { landmark: 'heel', event: 'off' },
  toe_contact: { landmark: 'toe', event: 'contact' },
  toe_off: { landmark: 'toe', event: 'off' },
};

export const ANNOTATION_TYPE_OPTIONS = [
  { value: 'heel_contact', label: 'Heel contact' },
  { value: 'heel_off', label: 'Heel off' },
  { value: 'toe_contact', label: 'Toe contact' },
  { value: 'toe_off', label: 'Toe off' },
];

const SHORTCUT_TO_TYPE = {
  z: 'heel_contact',
  x: 'toe_contact',
  c: 'heel_off',
  v: 'toe_off',
};

const SHORTCUT_TO_SIDE = {
  a: 'left',
  s: 'right',
};

/** Annotate-mode keys. Letter case does not matter; anything else is ignored. */
export function annotationTypeForShortcut(key) {
  if (typeof key !== 'string') return null;
  return SHORTCUT_TO_TYPE[key.toLowerCase()] || null;
}

/** A places the active event on the left foot, S on the right. */
export function annotationSideForShortcut(key) {
  if (typeof key !== 'string') return null;
  return SHORTCUT_TO_SIDE[key.toLowerCase()] || null;
}

export function contactColumnForTool(side, typeKey) {
  const parts = TYPE_PARTS[typeKey];
  if (!parts) return null;
  if (side !== 'left' && side !== 'right') return null;
  return `${parts.landmark}_${side}_${parts.event}`;
}

export function createContactAnnotation(side, typeKey, time) {
  const parts = TYPE_PARTS[typeKey];
  const column = contactColumnForTool(side, typeKey);
  if (!parts || !column) return null;
  if (typeof time !== 'number' || !Number.isFinite(time)) return null;
  return {
    id: `${column}@${time.toFixed(3)}`,
    column,
    side,
    landmark: parts.landmark,
    event: parts.event,
    typeKey,
    t: time,
    source: 'user',
  };
}

/**
 * Place the active event at the playhead on one foot, or remove it when that
 * foot already has the event within 50 ms.
 */
export function toggleAnnotationAtPlayhead(list, side, typeKey, time) {
  const base = Array.isArray(list) ? list : [];
  if (side !== 'left' && side !== 'right') return base;
  if (typeof time !== 'number' || !Number.isFinite(time)) return base;
  const annotation = createContactAnnotation(side, typeKey, time);
  if (!annotation) return base;
  const existing = base.find((item) => {
    if (!item || item.column !== annotation.column) return false;
    const itemTime = Number(item.t);
    return Number.isFinite(itemTime) && Math.abs(itemTime - time) <= 0.05;
  });
  if (existing) return base.filter((item) => item !== existing);
  return upsertContactAnnotation(base, annotation);
}

/** Replace a mark on the same column within 50 ms; otherwise append. */
export function upsertContactAnnotation(list, annotation) {
  const base = Array.isArray(list) ? list : [];
  if (!annotation || !annotation.column || typeof annotation.t !== 'number') return base;
  const next = base.filter((item) => {
    if (!item || item.column !== annotation.column) return true;
    const itemTime = Number(item.t);
    if (!Number.isFinite(itemTime)) return true;
    return Math.abs(itemTime - annotation.t) > 0.05;
  });
  next.push(annotation);
  return next;
}

export function removeContactAnnotation(list, id) {
  if (!Array.isArray(list)) return [];
  return list.filter((item) => !item || item.id !== id);
}

export function annotationsToContactEvents(annotations) {
  if (!Array.isArray(annotations)) return [];
  const events = [];
  for (let i = 0; i < annotations.length; i += 1) {
    const mark = annotations[i];
    if (!mark || typeof mark.t !== 'number' || !Number.isFinite(mark.t)) continue;
    if (!mark.landmark || !mark.event || !mark.side || !mark.column) continue;
    events.push({
      t: mark.t,
      landmark: mark.landmark,
      event: mark.event,
      typeKey: mark.typeKey,
      side: mark.side,
      column: mark.column,
      source: 'user',
      id: mark.id,
    });
  }
  return events;
}

export function mergeAnnotationEvents(pipelineEvents, annotations) {
  const base = Array.isArray(pipelineEvents) ? pipelineEvents.slice() : [];
  const extra = annotationsToContactEvents(annotations);
  const out = base.concat(extra);
  out.sort((a, b) => (a.t - b.t) || String(a.typeKey || '').localeCompare(String(b.typeKey || '')));
  return out;
}

const COLUMN_PATTERN = /^(heel|toe)_(left|right)_(contact|off)$/;

function userFrameSets(annotationFrames) {
  const user = annotationFrames && typeof annotationFrames === 'object'
    ? annotationFrames.user
    : null;
  const sets = {};
  if (!user || typeof user !== 'object') return sets;
  Object.keys(user).forEach((column) => {
    const frames = user[column];
    if (!Array.isArray(frames)) return;
    const set = new Set();
    frames.forEach((frame) => {
      const n = Number(frame);
      if (Number.isInteger(n) && n >= 0) set.add(n);
    });
    if (set.size) sets[column] = set;
  });
  return sets;
}

/**
 * Split angle-series contact events into pipeline samples and clinician marks.
 * `annotationFrames` is `trials.contact_annotation_frames` (`user` / `pipeline`
 * frame lists). A listed user frame that is missing from the series is still
 * restored, at `frame / fps`.
 */
export function splitContactEventsBySavedFrames(events, annotationFrames, fps) {
  const userFrames = userFrameSets(annotationFrames);
  const rate = typeof fps === 'number' && Number.isFinite(fps) && fps > 0 ? fps : 60;
  const pipeline = [];
  const annotations = [];
  const seen = new Set();
  const list = Array.isArray(events) ? events : [];
  for (let i = 0; i < list.length; i += 1) {
    const ev = list[i];
    if (!ev) continue;
    const frames = userFrames[ev.column];
    const frame = ev.frame;
    if (frames && Number.isInteger(frame) && frames.has(frame)) {
      const mark = createContactAnnotation(ev.side, ev.typeKey, ev.t);
      if (mark) {
        annotations.push(mark);
        seen.add(`${ev.column}@${frame}`);
      }
      continue;
    }
    const rest = { ...ev };
    delete rest.id;
    if (rest.source === 'user') delete rest.source;
    pipeline.push(rest);
  }
  Object.keys(userFrames).forEach((column) => {
    const parts = COLUMN_PATTERN.exec(column);
    if (!parts) return;
    userFrames[column].forEach((frame) => {
      const key = `${column}@${frame}`;
      if (seen.has(key)) return;
      annotations.push(
        createContactAnnotation(parts[2], `${parts[1]}_${parts[3]}`, frame / rate)
      );
    });
  });
  annotations.sort((a, b) => a.t - b.t || String(a.column).localeCompare(String(b.column)));
  return { pipeline, annotations };
}

function annotationMark(mark) {
  return !!(
    mark
    && mark.column
    && typeof mark.t === 'number'
    && Number.isFinite(mark.t)
  );
}

function annotationMarksMatch(local, remote) {
  if (local.id && remote.id && local.id === remote.id) return true;
  if (local.column !== remote.column) return false;
  return Math.abs(local.t - remote.t) <= 0.05;
}

/**
 * Compare the working set with the last marks stored on the server.
 * `saved` is the server count. `unsaved` is local marks that are not among them.
 * `dirty` is true when a save would change what the server has.
 */
export function annotationSaveCounts(current, saved) {
  const local = (Array.isArray(current) ? current : []).filter(annotationMark);
  const remote = (Array.isArray(saved) ? saved : []).filter(annotationMark);
  const used = new Set();
  let matched = 0;
  local.forEach((mark) => {
    const idx = remote.findIndex((item, index) => (
      !used.has(index) && annotationMarksMatch(mark, item)
    ));
    if (idx < 0) return;
    used.add(idx);
    matched += 1;
  });
  const unsaved = local.length - matched;
  return {
    unsaved,
    saved: remote.length,
    dirty: unsaved > 0 || matched !== remote.length,
  };
}

export function contactAnnotationsPayload(trialId, annotations) {
  return {
    trial_id: trialId,
    events: annotationsToContactEvents(annotations).map((ev) => ({
      name: ev.column,
      t: ev.t,
      landmark: ev.landmark,
      side: ev.side,
      event: ev.event,
      source: 'user',
    })),
  };
}
